import { FC, useMemo } from 'react';
import { Link, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { cn } from '@shadcn/ui/lib/utils';
import { ActionCardList } from './components/action-card-list/action-card-list';
import { Bot } from 'lucide-react';
import { Button } from '@shadcn/ui/components/ui/button';
import { motion } from 'motion/react';
import { ActionChatRuntimeProvider } from './components/action-chat/action-chat-runtime-provider';
import { ActionAgentPromptInput } from './components/action-agent-prompt-input';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GqlQueryVariablesProvider } from './hooks/use-gql-query-variables';
import { useSdk } from '../../sdk/use-sdk';
import { useQuery as useTanQuery } from '@tanstack/react-query';
import { Progress } from '@shadcn/ui/components/ui/progress';
import { GET_TOTAL_NUMBER_OF_CONTACTS } from '@zaplify/graphql';
import { useQuery } from '@apollo/client';
import { HoverCard } from '@shadcn/ui/components/ui/hover-card';
import { HoverCardTrigger } from '@shadcn/ui/components/ui/hover-card';
import { HoverCardContent } from '@shadcn/ui/components/ui/hover-card';
import { useAuth } from '../../providers/authentication-provider';

export const ActionsPageV2: FC = () => {
    const { contactId, actionCardType } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const {
        authState: { isGlobalAdmin },
    } = useAuth();
    const threadId = searchParams.get('threadId') || undefined;
    const {
        linkedinConnections: { getConnectionSyncProgress },
    } = useSdk();

    const { data: connectionSyncProgress, isLoading: loadingConnectionSync } = useTanQuery(getConnectionSyncProgress());
    const { data: connections, loading: loadingConnections } = useQuery(GET_TOTAL_NUMBER_OF_CONTACTS);

    // Check if we're on a chat route or other routes that don't need contactId
    const isOnChatRoute = location.pathname.includes('/actions-v2/chat');
    const shouldShowOutlet = contactId || isOnChatRoute;

    const progress = useMemo(() => {
        if (loadingConnectionSync || loadingConnections) return 0;
        const exactProgress = Math.min(
            1,
            connections?.Contacts_aggregate?.aggregate?.count / (connectionSyncProgress?.totalConnections * 0.9)
        );
        return Number(exactProgress.toFixed(2));
    }, [connectionSyncProgress, connections, loadingConnectionSync, loadingConnections]);
    const isLoadingProgress = loadingConnectionSync || loadingConnections;

    return (
        <GqlQueryVariablesProvider>
            <ActionChatRuntimeProvider>
                <div className="flex h-full max-w-full overflow-hidden">
                    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                        <div className="flex flex-col">
                            <div className={cn('flex flex-col gap-2 border-b p-4')}>
                                <HoverCard>
                                    <HoverCardTrigger>
                                        <h1 className="text-2xl font-medium">Actions</h1>
                                    </HoverCardTrigger>
                                    <HoverCardContent
                                        align="start"
                                        className={cn(
                                            !isGlobalAdmin && import.meta.env.MODE !== 'development' && 'hidden'
                                        )}
                                    >
                                        <div className="flex flex-col gap-2">
                                            <Progress value={progress * 100} className="h-2" />
                                            <span className="text-sm text-muted-foreground">
                                                Number of connections: {connectionSyncProgress?.totalConnections}
                                                <br />
                                                Number of synced connections:{' '}
                                                {connectionSyncProgress?.syncedConnections}
                                                <br />
                                                Number of synced profiles: {connectionSyncProgress?.syncedProfiles}
                                                <br />
                                                Number of contacts: {connections?.Contacts_aggregate?.aggregate?.count}
                                            </span>
                                        </div>
                                    </HoverCardContent>
                                </HoverCard>

                                <motion.div
                                    className="flex flex-row gap-2 w-[min(70dvw,60rem)] max-w-full self-center overflow-hidden"
                                    animate={{
                                        height: isOnChatRoute ? 0 : 'auto',
                                        opacity: isOnChatRoute ? 0 : 1,
                                    }}
                                    transition={{
                                        duration: 0.1,
                                        ease: 'easeInOut',
                                    }}
                                >
                                    <ActionAgentPromptInput />
                                </motion.div>
                            </div>
                            <ActionCardList />
                        </div>
                    </div>
                    <div
                        className={cn(
                            'flex flex-col border-l max-w-2/3 overflow-hidden h-full max-h-full justify-end w-0 transition-[width] duration-300 ease-in-out',
                            {
                                'w-2/3': shouldShowOutlet,
                            }
                        )}
                    >
                        <Outlet />
                    </div>
                </div>
            </ActionChatRuntimeProvider>
        </GqlQueryVariablesProvider>
    );
};

export default ActionsPageV2;
